import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents, scheduleAnchorDay } from '@servwell/shared';
import { dateOnly, dateOnlyMs, ordinal } from '../../lib/format';
import { useTRPC } from '../../lib/trpc';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { StatusBadge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { IconPlus, IconSchedule } from '../icons';
import { AGREEMENT_STATUS, display } from '../status';
import { NewAgreementSheet } from '../create/NewAgreementSheet';
import { useOrgClock } from '../../lib/useOrgClock';

/**
 * Maintenance agreements, as "Repeating work" (PRD v2 §4 — no new nav item; recurring work
 * belongs with Jobs, because that is what it turns into).
 *
 * "Agreement" is the word in the data model and the contract; it is not the word a shop
 * owner uses. On screen this is repeating work that generates visits, which is the fact
 * they act on.
 */

export type Agreement = {
  id: string;
  name: string;
  status: string;
  customerId: string;
  /** Joined by the server — see the note on listJobs. */
  customerName: string | null;
  startDate: string;
  endDate: string | null;
  billingMode: string;
  priceCents: number;
  schedule: unknown;
  createdAt: string | Date;
};

const BILLING_LABEL: Record<string, string> = {
  per_visit: 'Each visit',
  monthly: 'Monthly',
  annual: 'Yearly',
};

/**
 * The schedule is stored as jsonb; render it as a sentence, never as raw JSON.
 *
 * `startDate` is what makes the day-of-month knowable — the server derives the anchor from
 * it and clamps to the 28th, so a shop that asked for the 31st needs to be told on screen
 * why their visits land on the 28th. The clamp itself comes from shared, so the sentence
 * and the scheduler cannot disagree.
 */
export function scheduleText(schedule: unknown, startDate?: string): string {
  const s = (schedule ?? {}) as {
    freq?: string;
    interval?: number;
    months?: number[];
    dayOfMonth?: number;
  };
  const every = s.interval && s.interval > 1 ? `Every ${s.interval} ` : 'Every ';
  const unit =
    s.freq === 'yearly'
      ? s.interval && s.interval > 1
        ? 'years'
        : 'year'
      : s.interval && s.interval > 1
        ? 'months'
        : 'month';
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const when = s.months?.length
    ? ` in ${s.months.map((m) => monthNames[m - 1] ?? m).join(', ')}`
    : '';
  const anchor = startDate ? scheduleAnchorDay(s, startDate) : s.dayOfMonth;
  const day = anchor ? `, on the ${ordinal(anchor)}` : '';
  return `${every}${unit}${when}${day}`;
}

export function RepeatingWorkPanel() {
  // A panel, not a page, so it does not gate itself on the clock the way WithOrgClock does —
  // the list of contracts is perfectly readable without one. Only the CREATE sheet needs it,
  // because a contract's start date defaults to today, so the sheet waits instead.
  const { tz } = useOrgClock();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const agreements = useQuery(trpc.agreement.list.queryOptions());
  const [creating, setCreating] = useState(false);

  if (agreements.isPending) return <Skeleton className="h-40" />;
  if (agreements.isError) {
    return (
      <ErrorState
        icon={IconSchedule}
        what="repeating work"
        onRetry={() => void agreements.refetch()}
      />
    );
  }

  const rows = agreements.data as Agreement[];

  const columns: DataTableColumn<Agreement>[] = [
    {
      key: 'name',
      label: 'What',
      value: (a) => a.name,
      cell: (a) => (
        <div>
          <p className="font-medium">{a.name}</p>
          <p className="text-xs text-muted-foreground">
            {scheduleText(a.schedule, a.startDate)}
          </p>
        </div>
      ),
    },
    {
      key: 'customer',
      label: 'For',
      value: (a) => a.customerName,
      cell: (a) => dash(a.customerName),
    },
    {
      key: 'status',
      label: 'Status',
      value: (a) => a.status,
      cell: (a) => {
        const d = display(AGREEMENT_STATUS, a.status);
        return <StatusBadge tone={d.tone}>{d.label}</StatusBadge>;
      },
    },
    {
      key: 'price',
      label: 'Price',
      // A zero price is a real answer here (billed some other way), so it is not a dash.
      value: (a) => a.priceCents,
      cell: (a) => (
        <span className="tabular-nums">
          {formatCents(a.priceCents)}
          <span className="ml-1 text-xs text-muted-foreground">
            {BILLING_LABEL[a.billingMode] ?? a.billingMode}
          </span>
        </span>
      ),
      align: 'right',
    },
    {
      key: 'start',
      label: 'Started',
      value: (a) => dateOnlyMs(a.startDate),
      searchValue: (a) => dateOnly(a.startDate),
      cell: (a) => dateOnly(a.startDate),
      align: 'right',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Work that comes back — a service plan, a seasonal tune-up, a quarterly filter
          change. Servwell books the visits ahead so nobody has to remember.
        </p>
        <Button disabled={!tz} onClick={() => setCreating(true)}>
          <IconPlus size={15} />
          New repeating job
        </Button>
      </div>

      <DataTable
        tableId="agreements"
        columns={columns}
        rows={rows}
        rowKey={(a) => a.id}
        onRowClick={(a) =>
          navigate({ to: '/agreements/$agreementId', params: { agreementId: a.id } })
        }
        rowLabel={(a) => `Open ${a.name}`}
        empty={
          <EmptyState
            icon={IconSchedule}
            title="No repeating work yet"
            description="Set one up and the visits appear on the schedule by themselves — twice a year, every quarter, however the plan runs."
            action={
              <Button disabled={!tz} onClick={() => setCreating(true)}>
                New repeating job
              </Button>
            }
          />
        }
      />

      {tz ? (
        <NewAgreementSheet open={creating} onClose={() => setCreating(false)} tz={tz} />
      ) : null}
    </div>
  );
}
